import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import Button from "./Button";

function getHeaders() {
	const token = localStorage.getItem("token");
	return token ? { Authorization: `Bearer ${token}` } : {};
}

function SecurityDetailsDialog({ open, onClose, data, initialIndex }) {
	const [details, setDetails] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [showRaw, setShowRaw] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);

	const currentItem = useMemo(() => {
		if (!data || data.length === 0) return null;
		return data[currentIndex];
	}, [data, currentIndex]);

	const secId = useMemo(() => currentItem?.SecurityID, [currentItem]);

	const fetchDetails = useCallback(async () => {
		if (!secId) return;
		setIsLoading(true);
		setError("");
		setDetails(null);
		try {
			const res = await fetch(
				`/v1/securities/${secId}?idtype=msid&responseViewFormat=json&currencyId=EUR&languageId=it-IT&viewId=MFsnapshot`,
				{ headers: getHeaders() }
			);
			if (res.ok) {
				const arr = await res.json();
				setDetails(Array.isArray(arr) && arr.length > 0 ? arr[0] : arr);
			} else if (res.status === 401) {
				setError("Sessione scaduta. Effettua di nuovo il login.");
			} else {
				setError(`Errore HTTP ${res.status}`);
			}
		} catch {
			setError("Errore di connessione");
		} finally {
			setIsLoading(false);
		}
	}, [secId]);

	useEffect(() => {
		if (open && secId) {
			setCurrentIndex(initialIndex || 0);
			fetchDetails();
		}
	}, [open, secId, initialIndex, fetchDetails]);

	useEffect(() => {
		if (!open) { setShowRaw(false); setError(""); }
	}, [open]);

	const handlePrev = useCallback(() => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		}
	}, [currentIndex]);

	const handleNext = useCallback(() => {
		if (data && currentIndex < data.length - 1) {
			setCurrentIndex(currentIndex + 1);
		}
	}, [currentIndex, data]);

	if (!open) return null;

	return (
		<dialog
			open
			className="fixed inset-0 z-[100] flex items-center justify-center w-screen h-screen m-0 max-w-none max-h-none border-0 bg-slate-900/60 backdrop-blur-sm backdrop-filter p-4"
			aria-modal="true"
			onCancel={onClose}
		>
			<div className="flex h-[85vh] w-full max-w-4xl flex-col rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
				{/* Header */}
				<div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
					<div className="flex items-center gap-3">
						<h2 className="text-lg font-semibold text-slate-900">Security Details</h2>
						{data && data.length > 1 && (
							<div className="flex items-center gap-1 text-sm text-slate-500">
								<button
									type="button"
									onClick={handlePrev}
									disabled={currentIndex === 0}
									className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-slate-100 disabled:opacity-30"
								>
									<ChevronLeft className="h-4 w-4" />
								</button>
								<span className="tabular-nums">{currentIndex + 1} / {data.length}</span>
								<button
									type="button"
									onClick={handleNext}
									disabled={currentIndex >= data.length - 1}
									className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-slate-100 disabled:opacity-30"
								>
									<ChevronRight className="h-4 w-4" />
								</button>
							</div>
						)}
					</div>
					<button type="button" onClick={onClose} className="text-2xl leading-none text-slate-400 hover:text-slate-600">&times;</button>
				</div>

				{/* Content */}
				<div className="flex-1 overflow-y-auto px-5 py-4">
					{isLoading ? (
						<div className="flex items-center justify-center py-20">
							<Loader2 className="h-8 w-8 animate-spin text-slate-400" />
						</div>
					) : error ? (
						<div className="rounded-md bg-red-50 p-4 text-sm text-red-600">{error}</div>
					) : details ? (
						<div className="space-y-6">
							{/* Basic Info */}
							<Section title="Basic Info">
								<Fields
									fields={[
										{ label: "ID", value: details.Id },
										{ label: "Name", value: details.Name },
										{ label: "Legal Name", value: details.LegalName },
										{ label: "ISIN", value: details.Isin },
										{ label: "Domicile", value: details.Domicile },
										{ label: "Inception Date", value: details.InceptionDate },
									]}
								/>
							</Section>

							{/* Pricing */}
							{details.LastPrice && (
								<Section title="Last Price">
									<Fields
										fields={[
											{ label: "Date", value: details.LastPrice.Date },
											{ label: "Value", value: details.LastPrice.Value },
											{ label: "Currency", value: details.LastPrice.Currency?.Id },
										]}
									/>
								</Section>
							)}

							{/* Fund NAVs */}
							{details.FundNetAssetValues?.length > 0 && (
								<Section title="Net Asset Values">
									{details.FundNetAssetValues.map((nav, i) => (
										<div key={i} className="mb-2 rounded bg-slate-50 p-2 text-xs">
											<Fields
												fields={[
													{ label: "Currency", value: nav.CurrencyId },
													{ label: "Date", value: nav.Date },
													{ label: "Day End", value: nav.DayEndValue },
													{ label: "Month End", value: nav.MonthEndValue },
												]}
											/>
										</div>
									))}
								</Section>
							)}

							{/* Ratings */}
							<Section title="Ratings">
								<Fields
									fields={[
										{ label: "SRI", value: details.KID?.SRI },
										{ label: "Collected SRRI", value: details.CollectedSRRI?.Rank },
										{ label: "Collected SRRI Date", value: details.CollectedSRRI?.Date },
									]}
								/>
							</Section>

							{/* Investment Strategy */}
							{details.InvestmentStrategy && (
								<Section title="Investment Strategy">
									<p className="whitespace-pre-wrap text-sm text-slate-700">{details.InvestmentStrategy}</p>
								</Section>
							)}

							{/* Manager */}
							{details.ManagerList?.length > 0 && (
								<Section title="Manager">
									{details.ManagerList.map((m, i) => (
										<div key={i} className="mb-2 rounded bg-slate-50 p-2 text-xs">
											<Fields
												fields={[
													{ label: "Name", value: `${m.GivenName || ""} ${m.FamilyName || ""}`.trim() },
													{ label: "Since", value: m.StartDate },
												]}
											/>
										</div>
									))}
								</Section>
							)}

							{/* Provider */}
							{details.ProviderCompany && (
								<Section title="Provider Company">
									<Fields
										fields={[
											{ label: "Name", value: details.ProviderCompany.Name },
											{ label: "Address", value: details.ProviderCompany.AddressLine1 },
											{ label: "City", value: details.ProviderCompany.City },
											{ label: "Country", value: details.ProviderCompany.Country },
											{ label: "Homepage", value: details.ProviderCompany.Homepage },
										]}
									/>
								</Section>
							)}

							{/* Trailing Performance */}
							{details.TrailingPerformance?.length > 0 && (
								<Section title="Trailing Performance">
									{details.TrailingPerformance.map((tp, i) => (
										<div key={i} className="mb-2">
											<p className="mb-1 text-xs font-semibold text-slate-500">{tp.Type}</p>
											<div className="flex flex-wrap gap-2">
												{(tp.Return || []).map((r, j) => (
													<span key={j} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs">
														<span className="font-medium text-slate-600">{r.TimePeriod}:</span>
														<span className="text-slate-800">{r.Value != null ? `${r.Value}%` : "—"}</span>
													</span>
												))}
											</div>
										</div>
									))}
								</Section>
							)}

							{/* Top Holdings */}
							{details.Portfolios?.[0]?.PortfolioHoldings?.length > 0 && (
								<Section title="Top Holdings">
									<div className="overflow-x-auto rounded border border-slate-200">
										<table className="w-full text-xs">
											<thead className="bg-slate-50">
												<tr className="border-b border-slate-200">
													<th className="px-2 py-1 text-left font-semibold text-slate-600">Name</th>
													<th className="px-2 py-1 text-right font-semibold text-slate-600">Weight</th>
												</tr>
											</thead>
											<tbody>
												{details.Portfolios[0].PortfolioHoldings.map((h, i) => (
													<tr key={h.Id || i} className="border-b border-slate-100">
														<td className="px-2 py-1 text-slate-800">{h.SecurityName}</td>
														<td className="px-2 py-1 text-right text-slate-600">{h.Weighting}%</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</Section>
							)}

							{/* Mifid */}
							{details.Mifid && (
								<Section title="MIFID">
									<Fields
										fields={[
											{ label: "Ongoing Cost", value: details.Mifid.OngoingCostActual || details.Mifid.OngoingCostEstimated },
											{ label: "Entry Cost", value: details.Mifid.EntryCost },
											{ label: "Exit Cost", value: details.Mifid.ExitCost },
											{ label: "Management Fee", value: details.Mifid.ManagementFeeActual || details.Mifid.ManagementFeeEstimated },
											{ label: "Performance Fee", value: details.Mifid.PerformanceFeeActual },
										]}
									/>
								</Section>
							)}

							{/* Fund Attributes */}
							{details.FundAttributes && (
								<Section title="Fund Attributes">
									<Fields
										fields={Object.entries(details.FundAttributes).map(([k, v]) => ({ label: k, value: v }))}
									/>
								</Section>
							)}

							{/* Raw JSON */}
							<div className="border-t border-slate-200 pt-3">
								<button
									type="button"
									onClick={() => setShowRaw(!showRaw)}
									className="text-xs font-medium text-blue-600 hover:text-blue-800"
								>
									{showRaw ? "Hide" : "Show"} Raw JSON
								</button>
								{showRaw && (
									<pre className="mt-2 max-h-96 overflow-auto rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
										{JSON.stringify(details, null, 2)}
									</pre>
								)}
							</div>
						</div>
					) : null}
				</div>

				{/* Footer */}
				<div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
					<Button variant="outline" onClick={onClose}>Close</Button>
				</div>
			</div>
		</dialog>
	);
}

function Section({ title, children }) {
	return (
		<div>
			<h3 className="mb-2 text-sm font-semibold text-slate-800">{title}</h3>
			{children}
		</div>
	);
}
Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

function Fields({ fields }) {
	return (
		<div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
			{fields.filter((f) => f.value != null && f.value !== "").map((f, i) => (
				<div key={i} className="text-xs">
					<span className="text-slate-500">{f.label}: </span>
					<span className="font-medium text-slate-800">{String(f.value)}</span>
				</div>
			))}
		</div>
	);
}
Fields.propTypes = { fields: PropTypes.array.isRequired };

SecurityDetailsDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	data: PropTypes.array,
	initialIndex: PropTypes.number,
};

export default SecurityDetailsDialog;
